import { Box, Text } from '@chakra-ui/react-v2';
import type { BoxProps, ButtonProps } from '@chakra-ui/react-v2';
import { forwardRef, type PropsWithChildren } from 'react';

import { type SortDirection } from '@frontend/legacy-components';
import { CaretDownIcon } from '@frontend/legacy-components/src/components/mediaAndIcons/icons/iconSet';

export type TableFilterHeaderCellContentProps = BoxProps;

export const TableFilterHeaderCellContent = forwardRef<
  HTMLDivElement,
  TableFilterHeaderCellContentProps
>(({ children, ...restProps }, ref) => {
  return (
    <Box
      ref={ref}
      display="flex"
      flexDirection="row"
      height="2rem"
      alignItems="center"
      {...restProps}
    >
      {children}
    </Box>
  );
});
TableFilterHeaderCellContent.displayName = 'TableFilterHeaderCellContent';

export type TableFilterHeaderCellButtonProps = PropsWithChildren<unknown> &
  BoxProps &
  ButtonProps & {
    isDisabled?: boolean;
  };

export const TableFilterHeaderCellButton = forwardRef<
  HTMLDivElement,
  TableFilterHeaderCellButtonProps
>(({ isDisabled = false, children, ...restProps }, ref) => {
  return (
    <Box
      ref={ref}
      as="button"
      disabled={isDisabled}
      h="2rem"
      display="inline-flex"
      justifyContent="flex-start"
      alignItems="center"
      transitionProperty="common"
      transitionDuration="normal"
      _hover={{ bg: 'interaction.surfaceHover' }}
      _active={{ bg: 'interaction.surfaceActive' }}
      outline="none"
      _focusVisible={{ boxShadow: 'focusInset' }}
      _disabled={{ bg: 'initial', cursor: 'default' }}
      fontWeight="inherit"
      {...restProps}
    >
      {children}
    </Box>
  );
});
TableFilterHeaderCellButton.displayName = 'TableFilterHeaderCellButton';

export type TableFilterHeaderCellSortDisplayProps = BoxProps & {
  priority?: number;
  sortDirection?: SortDirection | null;
  sortPossibleDirections?: SortDirection[];
};

export const TableFilterHeaderCellSortDisplay = forwardRef<
  HTMLDivElement,
  TableFilterHeaderCellSortDisplayProps
>(
  (
    {
      priority,
      sortDirection = null,
      sortPossibleDirections = [],
      ...restProps
    },
    ref,
  ) => {
    return (
      <>
        <Box
          ref={ref}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          color="feature.placeholder"
          ml={0.75}
          w={1.5}
          h={3}
          {...restProps}
        >
          {sortPossibleDirections.includes('ascending') && (
            <CaretDownIcon
              boxSize={1.5}
              transform="rotate(180deg)"
              marginBottom={
                sortPossibleDirections.includes('descending')
                  ? '-2.5px'
                  : undefined
              }
              color={
                sortDirection === 'ascending' ? 'interaction.main' : 'inherit'
              }
            />
          )}
          {sortPossibleDirections.includes('descending') && (
            <CaretDownIcon
              boxSize={1.5}
              marginTop={
                sortPossibleDirections.includes('ascending')
                  ? '-2.5px'
                  : undefined
              }
              color={
                sortDirection === 'descending' ? 'interaction.main' : 'inherit'
              }
            />
          )}
        </Box>

        {priority != null && (
          <Text
            noOfLines={1}
            color="interaction.main"
            size="md"
            fontWeight="medium"
            m={0}
          >
            {priority}
          </Text>
        )}
      </>
    );
  },
);
TableFilterHeaderCellSortDisplay.displayName =
  'TableFilterHeaderCellSortDisplay';
